export type LoanCategory = "cash-flow" | "equipment" | "sba" | "industry";

export const loanCategories: { id: LoanCategory; label: string }[] = [
  { id: "cash-flow", label: "Working Capital & Cash Flow" },
  { id: "equipment", label: "Equipment & Asset Financing" },
  { id: "sba", label: "SBA Loans" },
  { id: "industry", label: "Industry-Specific" },
];

export type LoanProduct = {
  slug: string;
  name: string;
  category: LoanCategory;
  tagline: string;
  description: string;
  range: string;
  speed: string;
  bestFor: string[];
  /** Surface this product on the homepage and in the condensed footer list. */
  featured?: boolean;
};

export const loanProducts: LoanProduct[] = [
  // ---------- SBA ----------
  {
    slug: "sba-loans",
    name: "SBA Loans",
    category: "sba",
    tagline: "Government-backed loans with strong terms.",
    description:
      "SBA 7(a) and 504 programs offer larger amounts, longer terms, and lower rates. We handle the paperwork so you don't have to.",
    range: "$50K - $5M",
    speed: "30-60 days",
    bestFor: ["Expansion", "Real estate", "Refinancing debt"],
    featured: true,
  },
  {
    slug: "sba-7a-loan",
    name: "SBA 7(a) Loan",
    category: "sba",
    tagline: "The SBA's flagship program for growing businesses.",
    description:
      "Up to $5M for working capital, acquisition, refinancing, or real estate, backed by the Small Business Administration.",
    range: "$50K - $5M",
    speed: "30-60 days",
    bestFor: ["Working capital", "Acquisitions", "Refinancing"],
  },
  {
    slug: "sba-loan-for-daycare",
    name: "SBA Loan for Daycare",
    category: "sba",
    tagline: "Financing built for childcare operators.",
    description:
      "SBA-backed funding for licensed daycare and early childhood education businesses. Buy a center, expand, or refinance.",
    range: "$50K - $5M",
    speed: "30-60 days",
    bestFor: ["Daycare centers", "Childcare expansion"],
  },
  {
    slug: "sba-loan-for-franchise",
    name: "SBA Loan for Franchise",
    category: "sba",
    tagline: "Buy or expand an SBA-approved franchise.",
    description:
      "Financing for franchisees on the SBA Franchise Directory. Covers franchise fees, build-out, equipment, and working capital.",
    range: "$50K - $5M",
    speed: "30-60 days",
    bestFor: ["New franchisees", "Multi-unit operators"],
  },
  {
    slug: "sba-express-loan-for-veterans",
    name: "SBA Express Loan for Veterans",
    category: "sba",
    tagline: "Faster SBA financing for veteran-owned businesses.",
    description:
      "Streamlined SBA Express program with reduced fees and faster turnaround for qualifying veteran-owned small businesses.",
    range: "Up to $500K",
    speed: "Decisions in 36 hours",
    bestFor: ["Veteran-owned businesses"],
  },

  // ---------- Cash flow / working capital ----------
  {
    slug: "working-capital-loans",
    name: "Working Capital Loans",
    category: "cash-flow",
    tagline: "Cover day-to-day operating costs.",
    description:
      "Short-term financing to bridge cash flow gaps, cover payroll, restock inventory, or seize a time-sensitive opportunity.",
    range: "$10K - $500K",
    speed: "As fast as 24 hours",
    bestFor: ["Payroll gaps", "Inventory restock", "Seasonal swings"],
    featured: true,
  },
  {
    slug: "business-line-of-credit",
    name: "Business Line of Credit",
    category: "cash-flow",
    tagline: "Draw what you need, when you need it.",
    description:
      "A revolving credit line you can tap anytime. Pay interest only on what you use and reuse the line as you pay it back.",
    range: "$10K - $250K",
    speed: "1-3 business days",
    bestFor: ["Ongoing expenses", "Emergency reserves", "Flexibility"],
    featured: true,
  },
  {
    slug: "small-business-loans",
    name: "Small Business Loans",
    category: "cash-flow",
    tagline: "Term financing for established small businesses.",
    description:
      "Fixed-term loans for growth, hiring, marketing, or any general business purpose. Predictable payments and clear payoff.",
    range: "$25K - $500K",
    speed: "2-5 business days",
    bestFor: ["Growth", "Hiring", "Marketing"],
    featured: true,
  },
  {
    slug: "short-term-business-loans",
    name: "Short-Term Business Loans",
    category: "cash-flow",
    tagline: "Quick capital with terms of 3-18 months.",
    description:
      "Fast funding with shorter repayment windows, ideal when you need money now and can pay it back quickly.",
    range: "$5K - $250K",
    speed: "24-48 hours",
    bestFor: ["Bridge financing", "Quick projects"],
  },
  {
    slug: "merchant-cash-advance",
    name: "Merchant Cash Advance",
    category: "cash-flow",
    tagline: "Advance against future card sales.",
    description:
      "Get a lump sum today and repay through a small percentage of daily card receipts. Approval based on revenue, not credit.",
    range: "$5K - $500K",
    speed: "Same business day",
    bestFor: ["Retail", "Restaurants", "High-volume card sales"],
  },
  {
    slug: "unsecured-business-loans",
    name: "Unsecured Business Loans",
    category: "cash-flow",
    tagline: "No collateral required.",
    description:
      "Qualify based on business performance and credit. No need to pledge real estate, equipment, or personal assets.",
    range: "$10K - $250K",
    speed: "1-3 business days",
    bestFor: ["Asset-light businesses", "Service companies"],
  },
  {
    slug: "startup-business-loans",
    name: "Startup Business Loans",
    category: "cash-flow",
    tagline: "Capital for businesses under two years old.",
    description:
      "Programs designed for new ventures, including microloans, SBA startup options, and revenue-based products.",
    range: "$5K - $150K",
    speed: "2-7 business days",
    bestFor: ["New businesses", "First-time founders"],
  },
  {
    slug: "business-acquisition-loans",
    name: "Business Acquisition Loans",
    category: "cash-flow",
    tagline: "Finance the purchase of an existing business.",
    description:
      "Acquire a competitor, buy out a partner, or take over a turnkey operation. SBA and conventional options available.",
    range: "$100K - $5M",
    speed: "30-60 days",
    bestFor: ["Buyouts", "Acquisitions", "Succession"],
  },
  {
    slug: "accounts-receivable-financing",
    name: "Accounts Receivable Financing",
    category: "cash-flow",
    tagline: "Borrow against outstanding invoices.",
    description:
      "Use your A/R as collateral for a revolving line. Keep ownership of the invoices and collect from customers yourself.",
    range: "Up to 85% of A/R",
    speed: "3-5 business days",
    bestFor: ["B2B companies", "Long payment terms"],
  },
  {
    slug: "invoice-factoring",
    name: "Invoice Factoring",
    category: "cash-flow",
    tagline: "Turn unpaid invoices into cash.",
    description:
      "Sell outstanding receivables at a discount and get paid now instead of waiting 30, 60, or 90 days.",
    range: "Up to 90% of invoice value",
    speed: "24-48 hours",
    bestFor: ["B2B companies", "Steady invoicing"],
    featured: true,
  },
  {
    slug: "revenue-based-financing",
    name: "Revenue-Based Financing",
    category: "cash-flow",
    tagline: "Repay as a percentage of monthly revenue.",
    description:
      "Flexible repayment that flexes with your sales: pay more in strong months, less when revenue dips.",
    range: "$10K - $1M",
    speed: "2-5 business days",
    bestFor: ["SaaS", "Ecommerce", "Subscription businesses"],
  },
  {
    slug: "same-day-business-loans",
    name: "Same-Day Business Loans",
    category: "cash-flow",
    tagline: "Funding in hours, not weeks.",
    description:
      "Streamlined approval for businesses that need money in the bank today. Minimal paperwork, decisions in under an hour.",
    range: "$5K - $150K",
    speed: "Same business day",
    bestFor: ["Urgent repairs", "Last-minute orders", "Emergency cash"],
    featured: true,
  },
  {
    slug: "business-lenders-near-me",
    name: "Local Business Lenders",
    category: "cash-flow",
    tagline: "Work with a lender who knows your market.",
    description:
      "Local underwriting and relationship-driven service. Meet face-to-face and get decisions from people who know the area.",
    range: "$10K - $5M",
    speed: "Varies by program",
    bestFor: ["Local operators", "Relationship banking"],
  },
  {
    slug: "business-loan-broker",
    name: "Business Loan Broker",
    category: "cash-flow",
    tagline: "One application, many lenders.",
    description:
      "We shop your file across our network so you see the best offers side-by-side. No fee unless you fund.",
    range: "$5K - $5M",
    speed: "24-72 hours",
    bestFor: ["Comparison shopping", "Complex files"],
  },
  {
    slug: "commercial-loan-broker",
    name: "Commercial Loan Broker",
    category: "cash-flow",
    tagline: "Brokerage for larger commercial deals.",
    description:
      "Structured financing for commercial real estate, acquisitions, and large equipment packages, placed with the right lender.",
    range: "$250K - $25M+",
    speed: "30-90 days",
    bestFor: ["Commercial real estate", "Large acquisitions"],
  },

  // ---------- Equipment ----------
  {
    slug: "equipment-financing",
    name: "Equipment Financing",
    category: "equipment",
    tagline: "Buy the machinery, vehicles, or tech you need.",
    description:
      "The equipment itself secures the loan, so approvals are easier and rates are competitive. Own it outright when you finish paying.",
    range: "$25K - $2M",
    speed: "2-5 business days",
    bestFor: ["Vehicles", "Heavy machinery", "Tech and tools"],
    featured: true,
  },
  {
    slug: "manufacturing-equipment-financing",
    name: "Manufacturing Equipment Financing",
    category: "equipment",
    tagline: "CNC, presses, robotics, and production lines.",
    description:
      "Acquire or upgrade manufacturing equipment with terms that match the useful life of the asset.",
    range: "$50K - $5M",
    speed: "3-7 business days",
    bestFor: ["Manufacturers", "Fabricators"],
  },
  {
    slug: "agriculture-equipment-financing",
    name: "Agriculture Equipment Financing",
    category: "equipment",
    tagline: "Tractors, combines, and farm machinery.",
    description:
      "Seasonal-friendly payment structures for ag operators buying new or used equipment.",
    range: "$25K - $2M",
    speed: "3-7 business days",
    bestFor: ["Farms", "Ag operators"],
  },
  {
    slug: "landscaping-equipment-financing",
    name: "Landscaping Equipment Financing",
    category: "equipment",
    tagline: "Mowers, trucks, trailers, and irrigation gear.",
    description:
      "Get the crew rolling. Financing for landscaping equipment and work vehicles.",
    range: "$10K - $500K",
    speed: "2-5 business days",
    bestFor: ["Landscapers", "Lawn care companies"],
  },

  // ---------- Industry / vertical ----------
  {
    slug: "dental-practice-loans",
    name: "Dental Practice Loans",
    category: "industry",
    tagline: "Practice acquisition, build-out, and equipment.",
    description:
      "Financing built for dentists. Acquire a practice, open a second location, or finance chairs, imaging, and tech.",
    range: "$100K - $5M",
    speed: "2-4 weeks",
    bestFor: ["Dentists", "Specialty practices"],
  },
  {
    slug: "medical-practice-loans",
    name: "Medical Practice Loans",
    category: "industry",
    tagline: "Capital for physicians and clinics.",
    description:
      "Acquire a practice, expand, or buy medical equipment. Terms tailored to healthcare cash flow.",
    range: "$100K - $5M",
    speed: "2-4 weeks",
    bestFor: ["Physicians", "Clinics", "Specialty groups"],
  },
  {
    slug: "restaurant-loans",
    name: "Restaurant Loans",
    category: "industry",
    tagline: "Open, expand, or remodel your restaurant.",
    description:
      "Funding for build-outs, kitchen equipment, payroll, and working capital, designed for restaurant cash flow.",
    range: "$25K - $1M",
    speed: "2-7 business days",
    bestFor: ["Restaurants", "Bars", "Cafés"],
  },
  {
    slug: "trucking-business-loans",
    name: "Trucking Business Loans",
    category: "industry",
    tagline: "Trucks, trailers, and operating capital.",
    description:
      "Equipment and working capital programs for owner-operators and fleets, including factoring for freight invoices.",
    range: "$25K - $2M",
    speed: "2-5 business days",
    bestFor: ["Owner-operators", "Fleets"],
  },
  {
    slug: "commercial-construction-loan",
    name: "Commercial Construction Loan",
    category: "industry",
    tagline: "Ground-up and major renovation financing.",
    description:
      "Draw-based construction financing for commercial projects, with conversion to permanent financing at completion.",
    range: "$500K - $25M+",
    speed: "30-90 days",
    bestFor: ["Developers", "GCs", "Owner-builders"],
  },
  {
    slug: "hotel-loans",
    name: "Hotel Loans",
    category: "industry",
    tagline: "Acquisition, PIP, and refinance for hospitality.",
    description:
      "SBA 7(a)/504 and conventional financing for hotel acquisitions, brand-mandated renovations, and refinances.",
    range: "$500K - $10M+",
    speed: "30-90 days",
    bestFor: ["Hoteliers", "Hospitality groups"],
  },
  {
    slug: "salon-business-loans",
    name: "Salon Business Loans",
    category: "industry",
    tagline: "Build out, stock, and grow your salon.",
    description:
      "Financing for salon and spa owners: build-outs, equipment, inventory, and working capital.",
    range: "$10K - $500K",
    speed: "2-5 business days",
    bestFor: ["Salons", "Spas", "Barbershops"],
  },
  {
    slug: "retail-business-loans",
    name: "Retail Business Loans",
    category: "industry",
    tagline: "Inventory, build-out, and seasonal working capital.",
    description:
      "Term loans, lines, and MCAs structured for retail cash flow, including seasonal repayment options.",
    range: "$10K - $500K",
    speed: "1-5 business days",
    bestFor: ["Brick-and-mortar retail", "Boutiques"],
  },
  {
    slug: "auto-repair-shop-financing",
    name: "Auto Repair Shop Financing",
    category: "industry",
    tagline: "Lifts, diagnostics, and shop expansion.",
    description:
      "Equipment financing and working capital for independent auto repair, body shops, and tire stores.",
    range: "$10K - $500K",
    speed: "2-5 business days",
    bestFor: ["Auto repair", "Body shops"],
  },
  {
    slug: "ecommerce-business-funding",
    name: "Ecommerce Business Funding",
    category: "industry",
    tagline: "Inventory and ad spend for online sellers.",
    description:
      "Revenue-based and inventory financing for Shopify, Amazon, and DTC brands, funded against platform sales data.",
    range: "$10K - $1M",
    speed: "24-72 hours",
    bestFor: ["Shopify", "Amazon", "DTC brands"],
  },
  {
    slug: "food-truck-financing",
    name: "Food Truck Financing",
    category: "industry",
    tagline: "Truck, build-out, and commissary costs.",
    description:
      "Equipment financing for the truck itself plus working capital for permits, build-out, and opening inventory.",
    range: "$25K - $250K",
    speed: "2-7 business days",
    bestFor: ["Food trucks", "Mobile concepts"],
  },
  {
    slug: "loan-for-gym-business",
    name: "Gym & Fitness Business Loans",
    category: "industry",
    tagline: "Equipment, build-out, and member growth capital.",
    description:
      "Financing for gym owners: equipment leases, build-outs, and working capital for member acquisition.",
    range: "$25K - $1M",
    speed: "3-7 business days",
    bestFor: ["Gyms", "Studios", "Fitness franchises"],
  },
  {
    slug: "farm-credit-financing",
    name: "Farm Credit Financing",
    category: "industry",
    tagline: "Operating lines and equipment for ag producers.",
    description:
      "Operating loans, equipment financing, and real estate loans structured to ag production cycles.",
    range: "$25K - $5M",
    speed: "1-3 weeks",
    bestFor: ["Farms", "Ranches", "Producers"],
  },
  {
    slug: "women-owned-business-funding",
    name: "Women-Owned Business Funding",
    category: "industry",
    tagline: "Programs and grants for women entrepreneurs.",
    description:
      "Curated lenders, SBA programs, and grant resources for certified women-owned small businesses.",
    range: "$5K - $5M",
    speed: "Varies by program",
    bestFor: ["WBE/WOSB certified businesses"],
  },
];

export const featuredLoanProducts = loanProducts.filter((p) => p.featured);

export const getLoanProduct = (slug: string) =>
  loanProducts.find((p) => p.slug === slug);

export const loanProductsByCategory = loanCategories.map((c) => ({
  ...c,
  products: loanProducts.filter((p) => p.category === c.id),
}));
